<template>
	<div class="banefits py-6 md:py-16 !-mx-5 md:!mx-0 overflow-hidden" v-if="features">
		<div class="benefits-inner mx-auto px-4">
			<div 
				ref="scrollContainer"
				class="flex flex-nowrap items-start md:flex-wrap hide-scrollbar -mx-4 px-4 md:px-0 md:mx-0 md:grid md:grid-cols-3 lg:grid-cols-5 gap-8 overflow-x-scroll md:overflow-x-hidden"
				@mouseenter="pauseScroll"
				@mouseleave="resumeScroll"
			>
			
				<div
					v-for="(feature, index) in displayFeatures"
					:key="`${feature.description}-${index}`"
					class="flex min-w-[240px] md:min-w-[0px] flex-row md:flex-col items-center text-center">
						<div
						class="min-w-[50px] min-h-[50px] w-[50px] h-[50px] md:w-[120px] md:h-[120px] 2xl:w-[150px] 2xl:h-[150px] mb-0 md:mb-4 bg-light-grey-2 rounded-full flex items-center justify-center"
						v-if="feature.icon"
						>
						<i 
						:class="feature.icon"
							class="text-[20px] md:text-[36px] 2xl:text-[50px] text-primary"
							></i>
						</div>
						<p class="text-sm md:text-base 3xl:text-md text-gray-700 text-left md:text-center ml-4 md:ml-0">
						{{ feature.description }}
						</p>
				</div>
			
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { CmsElementServiceFeatures as CmsElementServiceFeaturesType } from "~/composables/useCustom";

// Define the Feature type
interface Feature {
  icon: string;
  description: string;
  // Add any other properties specific to your feature
}

const props = defineProps<{
  content: CmsElementServiceFeaturesType;
}>();

const features: Feature[] = props.content.data.features;

// Reactive state for infinite scrolling
const scrollContainer = ref<HTMLElement | null>(null);
const isScrolling = ref(true);
const scrollSpeed = 1; // pixels per frame
const scrollInterval = ref<ReturnType<typeof setInterval> | null>(null);
const isMobile = ref(false);

// Create duplicated features for infinite scroll effect
const displayFeatures = computed(() => {
  if (!isMobile.value) {
    return features;
  }
  // Duplicate features to create seamless infinite scroll
  return [...features, ...features];
});

// Check if device is mobile
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768; // md breakpoint
};

// Start automatic scrolling
const startScroll = () => {
  if (!scrollContainer.value || !isMobile.value) return;
  
  scrollInterval.value = setInterval(() => {
    if (!scrollContainer.value || !isScrolling.value) return;
    
    const container = scrollContainer.value;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    
    // Reset scroll position when reaching the end of first set of features
    if (scrollLeft >= scrollWidth / 2) {
      container.scrollLeft = 0;
    } else {
      container.scrollLeft += scrollSpeed;
    }
  }, 16); // ~60fps
};

// Stop automatic scrolling
const stopScroll = () => {
  if (scrollInterval.value) {
    clearInterval(scrollInterval.value);
    scrollInterval.value = null;
  }
};

// Pause scrolling on hover
const pauseScroll = () => {
  isScrolling.value = false;
};

// Resume scrolling when mouse leaves
const resumeScroll = () => {
  isScrolling.value = true;
};

// Initialize scrolling on mount
onMounted(() => {
  checkMobile();
  if (isMobile.value) {
    startScroll();
  }
  
  // Listen for resize events
  window.addEventListener('resize', () => {
    const wasMobile = isMobile.value;
    checkMobile();
    
    // Start/stop scrolling based on screen size change
    if (isMobile.value && !wasMobile) {
      startScroll();
    } else if (!isMobile.value && wasMobile) {
      stopScroll();
    }
  });
});

// Cleanup on unmount
onUnmounted(() => {
  stopScroll();
  window.removeEventListener('resize', checkMobile);
});
</script>